// Quotes & presentations (CEO 2026-10-19).
//
// Every quote and presentation your account manager has sent — never drafts.
// Each row: number, project, date sent, status in plain words and a Review
// button. Review opens the SAME page the email link opens (/o/:uuid for a
// quote, /p/:uuid for a presentation), in this tab; Approve and Request
// changes live on that page and nowhere in the portal. Layout follows
// InvoicesListPage.
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthProvider";
import { PortalLayout } from "@/components/PortalLayout";
import { formatDate } from "@/lib/portal-orders";
import { PORTAL_QUOTES_KEY, QUOTE_PILL_CLASS, fetchPortalQuotes } from "@/lib/portal-quotes";

interface ReviewRow {
  key: string;
  name: string;
  projectName: string;
  sentAt: string | null;
  status: keyof typeof QUOTE_PILL_CLASS;
  statusLabel: string;
  reviewUrl: string;
}

function ReviewTable({ title, rows, empty }: { title: string; rows: ReviewRow[]; empty: string }) {
  return (
    <section className="mb-10">
      <h2 className="mb-4 font-display text-2xl font-bold tracking-tight text-brown-500">{title}</h2>
      <div className="relative overflow-x-auto rounded-lg border border-cream-200 bg-white shadow-sm">
        {rows.length === 0 ? (
          <p className="px-6 py-10 text-center text-brown-400">{empty}</p>
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-cream-200 text-xs font-semibold uppercase tracking-wider text-brown-300">
                <th scope="col" className="px-6 py-4">Name</th>
                <th scope="col" className="px-6 py-4">Project</th>
                <th scope="col" className="px-6 py-4">Sent</th>
                <th scope="col" className="px-6 py-4">Status</th>
                <th scope="col" className="px-6 py-4"><span className="sr-only">Review</span></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-b border-cream-100 last:border-b-0">
                  <td className="px-6 py-4 font-semibold text-ink">{r.name}</td>
                  <td className="px-6 py-4 text-brown-500">{r.projectName}</td>
                  <td className="px-6 py-4 text-sm text-brown-400">{formatDate(r.sentAt)}</td>
                  <td className="px-6 py-4">
                    <span className={"inline-block rounded px-2 py-1 text-[0.6875rem] " + QUOTE_PILL_CLASS[r.status]}>
                      {r.statusLabel}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    {/* Same tab, same page as the email — approval happens there. */}
                    <a
                      href={r.reviewUrl}
                      className="inline-flex min-h-[36px] items-center rounded border border-rule bg-white px-3 text-sm text-ink hover:bg-canvas"
                    >
                      Review
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

export function QuotesPage() {
  const { user } = useAuth();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: PORTAL_QUOTES_KEY,
    queryFn: fetchPortalQuotes,
  });
  if (!user) return null;

  const quotes: ReviewRow[] = (data?.quotes ?? []).map((q) => ({
    key: `q-${q.id}`,
    name: `Quote #${q.orderNumber}`,
    projectName: q.projectName,
    sentAt: q.sentAt,
    status: q.status,
    statusLabel: q.statusLabel,
    reviewUrl: q.reviewUrl,
  }));
  const presentations: ReviewRow[] = (data?.presentations ?? []).map((p) => ({
    key: `p-${p.id}`,
    name: `Presentation #${p.presentationNumber}`,
    projectName: p.projectName,
    sentAt: p.sentAt,
    status: p.status,
    statusLabel: p.statusLabel,
    reviewUrl: p.reviewUrl,
  }));

  return (
    <PortalLayout>
      <div className="mb-10">
        <Link to="/dashboard" className="mb-3 inline-block text-sm font-semibold text-ink underline-offset-2 hover:underline">
          ← Dashboard
        </Link>
        <h1 className="font-display text-4xl font-bold tracking-tight">Quotes &amp; presentations</h1>
        <p className="mt-2 text-brown-400">Everything your account manager has sent you to review and approve.</p>
      </div>

      {isLoading && <p className="px-6 py-16 text-center text-brown-300">Loading your quotes…</p>}
      {isError && (
        <div className="px-6 py-12 text-center">
          <p className="mb-3 text-rust-500">We couldn't load your quotes and presentations.</p>
          <button type="button" onClick={() => void refetch()} className="text-sm font-semibold text-ink underline-offset-2 hover:underline">
            Try again
          </button>
        </div>
      )}
      {data && (
        <>
          <ReviewTable title="Quotes" rows={quotes} empty="No quotes yet. They'll appear here as soon as your account manager sends one." />
          <ReviewTable
            title="Presentations"
            rows={presentations}
            empty="No presentations yet. They'll appear here as soon as your account manager sends one."
          />
        </>
      )}
    </PortalLayout>
  );
}
